import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseButtonIcon, imgs } from '../../../../public/Images';
import { useGetCarListingQuery } from '@/hook/retail-api-slice';
import Spinner from '@/components/Spin/Spinner';
import SwipeableDrawer from '@/components/shared/bottom-sheet/bottom-sheet.component';

import ReserveSeeCarBanner from '@/components/shared/banners/reserve-vs-see-banner/reserve-vs-see-banner.component';
import CarDisplayCardClient from '@/components/shared/car-details-card/car-details-car-client.component';
import Typography from '@/components/shared/typography/typography.component';
import { sortCars } from '@/utils';
import Image from 'next/image';
import { Box } from '@mui/material';
import colors from '@/theme/colors.module.scss';
import { setDisplayedListings } from '@/redux/reducers/car-listing.reducer';
import { CarListingData } from '@/types';
import FilterableContent, { TFilterTerm } from '@/utils/filterUtil';

const BottomSheetMobileAvailableCars = ({
    open,
    setOpen,
    priceToSearch,
}: {
    priceToSearch: number;
    open: boolean;
    setOpen: (flag: boolean) => void;
}) => {
    const { data, isLoading: isLoadingCarData } = useGetCarListingQuery();

    const { t } = useTranslation();
    const [displayedListings, setDisplayedListing] = useState<CarListingData[]>(
        []
    );

    if (!data) {
        setDisplayedListings([]);
    }

    useMemo(() => {
        if (data) {
            const filterableVehicles =
                new FilterableContent<CarListingData>().SetData(data.data);

            const filters: TFilterTerm<CarListingData>[] = [
                {
                    isEnabled: true,
                    keyName: 'sylndrSellingPrice',
                    keyValue: [
                        (priceToSearch * 4) / 5,
                        (priceToSearch * 6) / 5,
                    ],
                    compareType: 'range',
                },
            ];
            filterableVehicles.ResetFilters().SetFilters(filters);
            setDisplayedListing(filterableVehicles.ApplyFilters().filteredData);
        }
    }, [priceToSearch, data]);

    const sortedCars = useMemo(() => {
        if (displayedListings.length === 0) {
            return [];
        }
        return sortCars(displayedListings).map((item, index) => (
            <>
                <Box
                    key={item.id + index}
                    sx={{
                        height: 'fit-content',
                    }}
                >
                    <CarDisplayCardClient
                        id={item.id}
                        discountPrice={item.discountPrice}
                        isPurchaseInProgress={item.isPurchaseInProgress}
                        key={index + 'car_finance'}
                        cardTitle={`${t(item.make)} ${item.model} ${item.year}`}
                        carName={item.sfVehicleName}
                        trim={item.trim}
                        installmentPrice={item?.installmentPrice}
                        totalPrice={item.sylndrSellingPrice}
                        kilometers={item.kilometers}
                        year={item.year}
                        transmission={item.transmission}
                        images={item?.images.length !== 0 ? item?.images : imgs}
                        isReserved={item.isReserved}
                        make={item.make}
                        model={item.model}
                        sourceScreen={'Cars Listing'}
                        appliedFilter={false}
                        appliedSort={false}
                        position={index + 1}
                        isNewlyAdded={item.isNewlyAdded}
                    />
                </Box>

                {(displayedListings.length < 6 &&
                    index === displayedListings.length - 1) ||
                index === 4 ? (
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <ReserveSeeCarBanner variant={'banner'} />
                    </Box>
                ) : (
                    ''
                )}
            </>
        ));
    }, [displayedListings]);
    return (
        <Box>
            <SwipeableDrawer open={open} setOpen={setOpen}>
                <Box sx={{ height: '95vh', position: 'relative' }}>
                    <Box
                        sx={{
                            marginBottom: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography
                            variant="subtitle2Medium"
                            color={colors.gray900}
                        >
                            {`${sortedCars.length}  ${t(
                                'Sylndr certified cars sort'
                            )}`}
                        </Typography>
                        <Box
                            onClick={() => {
                                setOpen(false);
                            }}
                        >
                            <Image
                                src={CloseButtonIcon}
                                alt="close"
                                width={24}
                                height={24}
                            />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        {isLoadingCarData ? (
                            <Spinner style={{ height: '100vh' }} />
                        ) : (
                            <Box sx={{ padding: ' 3vw 0px' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        position: 'relative',
                                    }}
                                >
                                    <Box sx={{ width: '100%' }}>
                                        <Box>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: `repeat(${1}, 1fr)`,
                                                    gap: '12px 4px',
                                                    width: '100%',
                                                }}
                                            >
                                                {sortedCars}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </SwipeableDrawer>
        </Box>
    );
};

export default BottomSheetMobileAvailableCars;
